import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Users, Activity, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * BenchModeDialog — at-a-glance equity board for Little League coaches.
 * Shows every roster player with innings played in field, at-bats, last-inning played,
 * and a color-coded equity score so no kid gets accidentally benched all game.
 */
export default function BenchModeDialog({
  open,
  onOpenChange,
  team,
  game,
  gs,
  currentInning,
  teamName,
}) {
  const stats = useMemo(() => {
    if (!team?.players || !game) return [];

    const inningPositions = game.inning_positions || {};
    const playLog = game.play_log || [];
    const battingOrder = gs?.battingOrder || game.batting_order || [];
    const currentBatterIdx = gs?.currentBatterIndex ?? game.current_batter_index ?? 0;

    const inningsArr = Object.keys(inningPositions)
      .map(k => parseInt(k, 10))
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);
    const totalInningsTracked = inningsArr.length;

    return team.players.map(p => {
      // Innings in the field
      const fieldedInnings = [];
      for (const inn of inningsArr) {
        const posMap = inningPositions[inn] || {};
        const playedThisInning = Object.values(posMap).includes(p.id);
        if (playedThisInning) fieldedInnings.push(inn);
      }
      const lastInningPlayed = fieldedInnings.length > 0 ? Math.max(...fieldedInnings) : null;

      // At-bats (count plays by this player in play_log)
      const atBats = playLog.filter(entry => {
        const desc = entry.description || '';
        const m = desc.match(/- (.+?)(?:\s*\(|$)/);
        return m && m[1].trim() === p.name;
      }).length;

      // Lineup status
      const battingSlot = battingOrder.findIndex(id => id === p.id);
      const isInLineup = battingSlot >= 0;
      // On-deck detection: next 2 batters
      const orderLen = battingOrder.length || 1;
      const nextSlot = (currentBatterIdx + 1) % orderLen;
      const onDeckSlot = (currentBatterIdx + 2) % orderLen;
      let lineupStatus = 'bench';
      if (isInLineup) {
        if (battingSlot === currentBatterIdx) lineupStatus = 'at_bat';
        else if (battingSlot === nextSlot) lineupStatus = 'on_deck';
        else if (battingSlot === onDeckSlot) lineupStatus = 'in_hole';
        else lineupStatus = 'in_lineup';
      }

      // Equity heuristic: if they've played 0 innings AND are not in lineup → red
      // If < 50% of innings played AND not at bat → amber
      // Otherwise green
      let equity = 'good';
      if (fieldedInnings.length === 0 && !isInLineup) equity = 'none';
      else if (totalInningsTracked > 0 && fieldedInnings.length / totalInningsTracked < 0.5) equity = 'low';

      return {
        id: p.id,
        name: p.name,
        jersey: p.jersey_number || '',
        fielded_innings: fieldedInnings,
        fielded_count: fieldedInnings.length,
        last_inning_played: lastInningPlayed,
        at_bats: atBats,
        battingSlot: isInLineup ? battingSlot + 1 : null,
        lineupStatus,
        equity,
        totalInnings: totalInningsTracked,
      };
    }).sort((a, b) => {
      // Sort: most urgent first (equity none > low > good), then by at-bats asc
      const order = { none: 0, low: 1, good: 2 };
      if (order[a.equity] !== order[b.equity]) return order[a.equity] - order[b.equity];
      return a.at_bats - b.at_bats;
    });
  }, [team, game, gs, currentInning]);

  const benchedWithNoInnings = stats.filter(s => s.equity === 'none').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-stone-900 border-green-800 max-w-lg" data-testid="bench-mode-dialog">
        <DialogHeader>
          <DialogTitle className="text-green-300 text-center text-sm flex items-center justify-center gap-2">
            <Users size={16} /> BENCH MODE · {teamName}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-[11px] text-center">
            Playing-time equity · Inning {currentInning} · {stats.length} player{stats.length === 1 ? '' : 's'}
          </DialogDescription>
        </DialogHeader>

        {/* Alert strip */}
        {benchedWithNoInnings > 0 && (
          <div
            className="flex items-center gap-2 bg-red-900/40 border border-red-700/50 text-red-200 rounded-lg px-3 py-2 text-xs"
            data-testid="bench-mode-alert"
          >
            <AlertTriangle size={14} className="shrink-0" />
            <span>
              <strong>{benchedWithNoInnings} player{benchedWithNoInnings === 1 ? '' : 's'}</strong>{' '}
              haven&apos;t played yet. Consider subbing them in.
            </span>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center gap-3 text-[10px] text-stone-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Playing</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Low time</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Not played</span>
        </div>

        {/* Roster list */}
        <div className="max-h-[55vh] overflow-y-auto space-y-1.5" data-testid="bench-mode-list">
          {stats.length === 0 ? (
            <p className="text-stone-500 text-sm text-center py-6">No roster players yet.</p>
          ) : (
            stats.map(s => {
              const dotColor = s.equity === 'none' ? 'bg-red-500' : s.equity === 'low' ? 'bg-amber-500' : 'bg-emerald-500';
              const bgTone = s.equity === 'none' ? 'bg-red-950/30 border-red-800/60' :
                             s.equity === 'low' ? 'bg-amber-950/30 border-amber-800/60' :
                             'bg-stone-800/70 border-stone-700/60';
              return (
                <div
                  key={s.id}
                  className={cn("flex items-center gap-3 rounded-lg border px-3 py-2", bgTone)}
                  data-testid={`bench-mode-row-${s.id}`}
                >
                  <div className={cn("w-2 h-2 rounded-full shrink-0", dotColor)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {s.jersey && <span className="text-stone-500 text-[10px] w-6">#{s.jersey}</span>}
                      <span className="text-white text-sm font-semibold truncate">{s.name}</span>
                      {s.lineupStatus === 'at_bat' && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-stone-900 uppercase tracking-wider">
                          At Bat
                        </span>
                      )}
                      {s.lineupStatus === 'on_deck' && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-600/80 text-white uppercase tracking-wider">
                          On Deck
                        </span>
                      )}
                      {s.lineupStatus === 'in_hole' && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-stone-700 text-amber-300 uppercase tracking-wider">
                          In Hole
                        </span>
                      )}
                      {s.battingSlot && s.lineupStatus !== 'at_bat' && s.lineupStatus !== 'on_deck' && s.lineupStatus !== 'in_hole' && (
                        <span className="text-[9px] text-stone-400">#{s.battingSlot}</span>
                      )}
                      {!s.battingSlot && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-stone-700 text-stone-400 uppercase tracking-wider">
                          Bench
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-stone-400">
                      <span className="flex items-center gap-1">
                        <Clock size={10} /> {s.fielded_count} inn
                      </span>
                      <span className="flex items-center gap-1">
                        <Activity size={10} /> {s.at_bats} AB
                      </span>
                      {s.last_inning_played !== null && (
                        <span className="text-stone-500">Last: I{s.last_inning_played}</span>
                      )}
                      {s.equity === 'none' && <span className="text-red-300 font-semibold">Not played</span>}
                    </div>
                  </div>
                  {s.equity === 'good' && s.fielded_count > 0 && (
                    <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>

        <p className="text-stone-500 text-[10px] text-center">
          Tap &quot;Quick Sub&quot; to swap any bench player into the lineup.
        </p>

        <Button
          size="sm"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          className="text-stone-400 hover:text-white"
          data-testid="bench-mode-close"
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
}
